/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import { ALL_TIME } from "../../components/commonComponents/DateRangeFilter";
import { resetAppointmentFilters } from "./appointmentSlice";

const initialFilters = {
  opportunityType: [],
  opportunityStatus: [],
  opportunitySource: [],
  opportunitySearch: "",
  opportunityVOI: [],
  userTypesOr: [],
  userTypesAnd: [],
  // Existing field — written by the "Opportunity Owners" column FilterDropdown
  // (non-CRM workspaces). Kept so any legacy reader continues to work.
  ownerIds: [],
  // Page-level user selection — written by the Users/Teams EntityFilter and
  // by dashboard drill-ins. Mapped directly to the BE payload's
  // `assignedUserIds` field. Separate from `ownerIds` so the column filter's
  // "applied" indicator and Reset action don't interfere with the page-level
  // selection (and vice versa).
  assignedUserIds: [],
  // --- Carried over from the dashboard "by sales person / by source" tables ---
  // so the opportunity view reproduces the same scope the user drilled in from.
  // Mirrors the appointment slice (see appointmentSlice.js).
  // `viewMode` mirrors the waterfall / current-status toggle. Defaults to
  // "normal" (current status) for a direct land on the page; dashboard
  // drill-ins overwrite this with whatever the user was viewing.
  viewMode: "normal",
  // Date preset key — same value space as the dashboard's date filter.
  // Defaults to "All time" (no date window) so a direct land on the
  // opportunity view shows every opportunity rather than a date-bounded
  // slice. Dashboard drill-ins still overwrite this with whatever preset was
  // active there, so a drill-in keeps reproducing the dashboard's scope.
  filterType: ALL_TIME,
  // Explicit [startDate, endDate] window (used when filterType is CUSTOM/none).
  dateRange: [],
  // Metric basis ("created_date" / "updated_date"). Defaults to created_date
  // so the page shows opportunities by creation when landed on directly;
  // dashboard drill-ins overwrite based on the dashboard's basis toggle.
  dateType: "created_date",
  // Mirrors the dashboard's "Users / Teams" toggle. When `teams`, the query
  // sends `teamIds` and skips the user-side filters.
  entityMode: "users",
  // The mode the user has actually committed to. When it differs from
  // `entityMode`, the dropdown is in "preview" (show placeholder, not a chip).
  activeFilterMode: "users",
  teamIds: [],
  // Flipped on by a dashboard drill-in so the Lead Center opportunity view
  // can render a banner explaining the count difference (the dashboard
  // excludes deactivated users; this list doesn't). The page clears it
  // after capturing into local state.
  cameFromDashboard: false,
};
const initialState = {
  opportunityFilters: initialFilters,
  opportunityPresetFilter: {},
  // Set true by a dashboard drill-in so LeadCenter opens straight into the
  // Opportunity sub-tab (tableMode = 1). LeadCenter clears it after reading.
  forceOpportunityView: false,
};

const opportunitySlice = createSlice({
  name: "opportunity",
  initialState,
  reducers: {
    setOpportunityTypeFilter(state, action) {
      state.opportunityFilters.opportunityType = action.payload;
    },
    setOpportunitySourceFilter(state, action) {
      state.opportunityFilters.opportunitySource = action.payload;
    },
    setOpportunityStatusFilter(state, action) {
      state.opportunityFilters.opportunityStatus = action.payload;
    },
    setOpportunitySearchFilter(state, action) {
      state.opportunityFilters.opportunitySearch = action.payload;
    },
    resetOpportunityFilters(state, action) {
      state.opportunityFilters = initialFilters;
    },
    // A preset only stores the filters the Opportunity view lets you save
    // (type / status / source / VOI / owners), so it must be layered ON TOP of
    // the defaults rather than replacing the whole object. Assigning the
    // payload directly used to drop every key a preset doesn't carry —
    // viewMode, filterType, dateRange, dateType, entityMode, teamIds,
    // assignedUserIds — which silently moved the page from "Created on / All
    // time / Current status" to "Updated on / no date / Waterfall" on every
    // preset apply.
    setPresetOpportunityFilters(state, action) {
      state.opportunityFilters = { ...initialFilters, ...(action.payload || {}) };
    },
    setOpportunityPresetFilter(state, action) {
      state.opportunityPresetFilter = action.payload;
    },
    setOpportunityVoiFilter(state, action) {
      state.opportunityFilters.opportunityVOI = action.payload;
    },
    setOpportunityOwnersOr(state, action) {
      state.opportunityFilters.userTypesOr = action.payload;
      state.opportunityFilters.userTypesAnd = [];
    },
    setOpportunityOwnersAnd(state, action) {
      state.opportunityFilters.userTypesAnd = action.payload;
      state.opportunityFilters.userTypesOr = [];
    },
    setOpportunityOwnerIds(state, action) {
      state.opportunityFilters.ownerIds = action.payload;
    },
    setOpportunityAssignedUserIds(state, action) {
      state.opportunityFilters.assignedUserIds = action.payload;
    },
    setOpportunityViewMode(state, action) {
      state.opportunityFilters.viewMode = action.payload;
    },
    setOpportunityTeamIds(state, action) {
      state.opportunityFilters.teamIds = action.payload || [];
    },
    // Shallow-merge any subset of filters in one dispatch. Used by dashboard
    // drill-in flows (Opportunity-by-Sales-Person, Opportunity-by-Source) so
    // the call site passes one object instead of many individual dispatches.
    // Keys not present in the payload are left untouched.
    setOpportunityFilters(state, action) {
      Object.assign(state.opportunityFilters, action.payload || {});
    },
    // Drill-in toggle so LeadCenter lands on the Opportunity sub-tab.
    setForceOpportunityView(state, action) {
      state.forceOpportunityView = !!action.payload;
    },
  },
  // Cross-slice: when `appointmentSlice.resetAppointmentFilters` fires (logout
  // / Add Appointment), also clear the Opportunity Type & Status filters in
  // the same dispatch.
  extraReducers: builder => {
    builder.addCase(resetAppointmentFilters, state => {
      state.opportunityFilters.opportunityType = [];
      state.opportunityFilters.opportunityStatus = [];
      state.opportunityFilters.opportunitySource = [];
    });
  },
});

export const {
  setOpportunitySourceFilter,
  setOpportunityTypeFilter,
  setOpportunityStatusFilter,
  setOpportunitySearchFilter,
  resetOpportunityFilters,
  setPresetOpportunityFilters,
  setOpportunityPresetFilter,
  setOpportunityVoiFilter,
  setOpportunityOwnersAnd,
  setOpportunityOwnersOr,
  setOpportunityOwnerIds,
  setOpportunityAssignedUserIds,
  setOpportunityViewMode,
  setOpportunityTeamIds,
  setOpportunityFilters,
  setForceOpportunityView,
} = opportunitySlice.actions;

export default opportunitySlice.reducer;
